import React, { useState, useEffect } from 'react';
import { BookOpen, Lightbulb, Rocket, Target, Zap, Users, Video, FileText, Download, Palette, TrendingUp, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { getPublishedGuides } from '@/services/cms';
import SEOHead from '@/components/SEO/SEOHead';

const Guides = () => {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        setLoading(true);
        const data = await getPublishedGuides();
        setGuides(data);
      } catch (error) {
        console.error('Error fetching guides:', error);
        setError('Failed to load guides');
      } finally {
        setLoading(false);
      }
    };
    fetchGuides();
  }, []);
  
  const quickTips = [
    {
      icon: Lightbulb,
      title: 'Be Specific',
      description: 'The more detailed your prompt, the better the results.'
    },
    {
      icon: Palette,
      title: 'Experiment Freely',
      description: 'Try different styles and variations to find what works.'
    },
    {
      icon: Target,
      title: 'Use References',
      description: 'Describe specific styles, colors, or themes you want.'
    },
    {
      icon: Zap,
      title: 'Iterate & Refine',
      description: 'Use the regeneration feature to perfect your designs.'
    }
  ];

  // Icon mapping helper
  const getIconComponent = (iconName) => {
    const iconMap = {
      Rocket, Lightbulb, Palette, Video, Download, Award, Target, Zap, TrendingUp, BookOpen, FileText
    };
    return iconMap[iconName] || BookOpen;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title="Guides & Tutorials - Fluxara | Learn AI T-Shirt Design"
        description="Comprehensive guides and tutorials to help you create professional AI-generated T-shirt designs."
        keywords={['guides', 'tutorials', 'design tips', 'AI design', 't-shirt design']}
        canonicalUrl="/guides"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-100 rounded-2xl mb-4">
              <BookOpen className="w-8 h-8 text-cyan-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Learn & <span className="text-cyan-600">Master Design</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive guides and tutorials to help you create professional AI-generated T-shirt designs.
            </p>
          </div>

          {/* Quick Tips */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Quick Tips for Success</h2>
            <div className="grid md:grid-cols-4 gap-4">
              {quickTips.map((tip, index) => {
                const Icon = tip.icon;
                return (
                  <div key={index} className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <Icon className="w-8 h-8 text-cyan-600 mb-3" />
                    <h3 className="font-bold text-gray-900 mb-1">{tip.title}</h3>
                    <p className="text-sm text-gray-600">{tip.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {error && (
            <div className="text-center text-red-600 mb-8">
              {error}
            </div>
          )}

          {/* Guides Grid */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">All Guides & Tutorials</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((guide) => {
                const Icon = getIconComponent(guide.iconName);
                return (
                  <div
                    key={guide.id}
                    className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100 cursor-pointer"
                    onClick={() => navigate(`/guides/${guide.slug}`)}
                  >
                    <div className="flex items-start mb-4">
                      <div className={`${guide.color || 'bg-cyan-500'} p-3 rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="ml-4 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-cyan-600 uppercase tracking-wide">
                            {guide.guideType}
                          </span>
                          {guide.level && (
                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                              {guide.level}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mt-1">{guide.title}</h3>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4 text-sm leading-relaxed">{guide.description}</p>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 flex items-center">
                        <BookOpen className="w-4 h-4 mr-1" />
                        {guide.duration || '5 min read'}
                      </span>
                      <Button size="sm" variant="outline" className="text-cyan-600 hover:bg-cyan-50">
                        {guide.guideType === 'Video' ? 'Watch' : guide.guideType === 'Download' ? 'Download' : 'Read'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {guides.length === 0 && !loading && !error && (
              <div className="text-center text-gray-600">
                No guides available yet.
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="bg-gradient-to-r from-cyan-600 to-turquoise-600 rounded-2xl p-8 text-center text-white shadow-xl">
            <Users className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Need Help?</h2>
            <p className="text-lg mb-6 opacity-90 max-w-2xl mx-auto">
              Can't find what you're looking for? Our support team is here to help you succeed with Fluxara.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                size="lg" 
                variant="secondary" 
                className="bg-white text-cyan-600 hover:bg-gray-100"
                onClick={() => navigate('/support')}
              >
                Contact Support
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-cyan-600">
                Join Community
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Guides;